import { FC } from 'react';
import { Alert, Menu, Skeleton } from 'react-daisyui';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import SearchParams from '@/_constants/SearchParams.ts';
import { useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import useChatCreate from '@/api/chat/useChatCreate.ts';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/solid';

type NavbarProps = {
  onClick?: () => void;
  menuClassName?: string;
};

const NavbarChat: FC<PropsWithClassName<NavbarProps>> = ({ className, onClick, menuClassName }) => {
  const {
    logoutQuery: { mutateAsync: logout, isPending: isLoadingLogout },
  } = useAuthContext();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { menuOptions, isLoadingMenu } = useDefaultLayoutContext();
  const {
    mutateAsync: createChat,
    isPending: isLoadingCreateChat,
    isError: isErrorCreateChat,
    error: errorCreateChat,
  } = useChatCreate();

  const activeChatId = searchParams.get(SearchParams.CHAT_ID);

  const selectElement = (id: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.CHAT_ID, id);
      return prev;
    });
  };

  const handleChatClick = (id: string) => {
    selectElement(id);

    if (onClick) {
      onClick();
    }
  };

  const handleCreateChat = () => {
    void (async () => {
      try {
        if (isLoadingCreateChat) return;

        const response = await createChat({
          name: t('components.chat.newChat'),
        });

        selectElement(response.id);

        if (onClick) {
          onClick();
        }
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleLogout = () => {
    void (async () => {
      try {
        if (isLoadingLogout) return;
        await logout();
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <nav className={cn('flex flex-col gap-4 justify-between grow', className)}>
      <div className="flex flex-col grow gap-6">
        {isErrorCreateChat && <Alert status="error">{errorCreateChat?.message}</Alert>}
        <LoadingButton loading={isLoadingCreateChat} onClick={handleCreateChat} color="primary">
          {t('button.startNewChat')}
        </LoadingButton>
        <Menu className={cn('p-0 overflow-auto flex-nowrap grow h-1', menuClassName)}>
          {isLoadingMenu ? (
            <div className="flex flex-col gap-4">
              {[...Array(8)].map((_, index) => (
                <SkeletonMenuItem key={index} />
              ))}
            </div>
          ) : (
            menuOptions.map((item) => {
              return (
                <Menu.Item key={item.id}>
                  <button
                    title={item.name}
                    onClick={() => handleChatClick(item.id)}
                    className={cn('active:bg-primary-400', {
                      'bg-secondary-400 text-white': item.id === activeChatId,
                    })}
                  >
                    <span className="text-ellipsis line-clamp-1">{item.name}</span>
                  </button>
                </Menu.Item>
              );
            })
          )}
        </Menu>
      </div>
      <div className="flex flex-col">
        <LoadingButton
          loading={isLoadingLogout}
          onClick={handleLogout}
          color="primary"
          startIcon={<ArrowLeftStartOnRectangleIcon className="w-4" />}
        >
          {t('button.logout')}
        </LoadingButton>
      </div>
    </nav>
  );
};

function SkeletonMenuItem() {
  return <Skeleton className="w-full h-8" />;
}

export default NavbarChat;
